"use client";

import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { FaTimes, FaInfoCircle, FaBug, FaTools, FaCircle, FaTachometerAlt } from 'react-icons/fa';
import changelogJson from '@/changelogs.json';

interface ChangelogItem {
  description: string;
  type: 'feature' | 'bugfix' | 'improvement' | 'performance';
}

export interface ChangelogInfo {
  title: string;
  datetime: string;
  updates: ChangelogItem[];
}

export default function Changelog() {
  const t = useTranslations('changelog');
  const [isVisible, setIsVisible] = useState(false);
  const [unseenChangelogs, setUnseenChangelogs] = useState<[string, ChangelogInfo][]>([]);

  useEffect(() => {
    const allChangelogs = Object.entries(changelogJson) as [string, ChangelogInfo][];
    const sortedChangelogs = allChangelogs.sort((a, b) => {
      const versionA = a[0].split('.').map(Number);
      const versionB = b[0].split('.').map(Number);
      for (let i = 0; i < 3; i++) {
        if (versionA[i] !== versionB[i]) {
          return versionB[i] - versionA[i];
        }
      }
      return 0;
    });

    const lastSeenVersion = localStorage.getItem('changelog-last-seen');
    const unseenVersions = sortedChangelogs.filter(([version]) => {
      if (!lastSeenVersion) return true;
      
      const currentVer = version.split('.').map(Number);
      const lastVer = lastSeenVersion.split('.').map(Number);
      
      for (let i = 0; i < 3; i++) {
        if (currentVer[i] !== lastVer[i]) {
          return currentVer[i] > lastVer[i];
        }
      }
      return false;
    });

    if (unseenVersions.length > 0) {
      setUnseenChangelogs(unseenVersions);
      setIsVisible(true);
    }
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    if (unseenChangelogs.length > 0) {
      const latestVersion = unseenChangelogs[0][0];
      localStorage.setItem('changelog-last-seen', latestVersion);
    }
  };

  if (!isVisible || unseenChangelogs.length === 0) return null;

  const renderUpdateType = (type: string, items: ChangelogItem[]) => {
    const typeConfig = {
      feature: {
        icon: FaInfoCircle,
        label: t('features'),
        color: 'text-bme-main',
        iconColor: 'text-bme-main'
      },
      bugfix: {
        icon: FaBug,
        label: t('bugfixes'),
        color: 'text-red-600',
        iconColor: 'text-red-600'
      },
      improvement: {
        icon: FaTools,
        label: t('improvements'),
        color: 'text-blue-600',
        iconColor: 'text-blue-600'
      },
      performance: {
        icon: FaTachometerAlt,
        label: t('performance'),
        color: 'text-purple-600',
        iconColor: 'text-purple-600'
      }
    };

    const config = typeConfig[type as keyof typeof typeConfig];
    if (!config || items.length === 0) return null;

    const Icon = config.icon;

    return (
      <div className="mb-3">
        <div className="flex items-center gap-2 text-gray-600 mb-1">
          <Icon className={config.iconColor} />
          <h4 className="font-medium">{config.label}</h4>
        </div>
        <ul className="space-y-1 ml-6">
          {items.map((item, index) => (
            <li key={index} className="flex items-start gap-2">
              <FaCircle className="text-gray-400 text-[6px] mt-1.5 shrink-0" />
              <span className="text-gray-600 text-sm">{item.description}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <div 
      className="fixed inset-0 flex items-center justify-center z-50 bg-black/50 backdrop-blur-sm p-4" 
      onClick={handleClose}
    >
      <div 
        className="max-w-2xl w-full bg-white rounded-2xl shadow-xl border border-gray-200 max-h-[90vh] overflow-y-auto animate-slide-up" 
        onClick={e => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="flex items-start justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-800">
              {t('newUpdates')}
            </h3>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-lg hover:bg-gray-100"
              aria-label={t('close')}
            >
              <FaTimes className="h-5 w-5" />
            </button>
          </div>
          
          <div className="space-y-6">
            {unseenChangelogs.map(([version, changelog]) => (
              <div key={version} className="border-t border-gray-200 pt-6 first:border-t-0 first:pt-0">
                <h4 className="text-base font-medium text-gray-700 mb-4">
                  v{version} - {changelog.title}
                </h4>

                <div className="space-y-3">
                  {renderUpdateType('feature', changelog.updates.filter(item => item.type === 'feature'))}
                  {renderUpdateType('improvement', changelog.updates.filter(item => item.type === 'improvement'))}
                  {renderUpdateType('performance', changelog.updates.filter(item => item.type === 'performance'))}
                  {renderUpdateType('bugfix', changelog.updates.filter(item => item.type === 'bugfix'))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
